import { Input_V2_Mutations_RaptorJob_UpdateRaptorJobMutationInput } from '@/common/infrastructure/gql/generated/schema';
import { JobFormData } from '../types/JobTypes';
import { normalizeCallerId } from '@/common/application/utils/stringUtils';
import { formatDate } from '@/common/application/utils/timeUtils';

// Shared helper functions for job update mappers

/**
 * Validates and returns a date field formatted as yyyy-MM-dd, or undefined if invalid
 * Accepts both string and Date inputs for compatibility with FormikDatePicker
 */
export const validateDateField = (dateField?: string | Date): string | undefined => {
  if (!dateField) return undefined;
  return formatDate(dateField, 'yyyy-MM-dd');
};

/**
 * Maps a clearable dropdown field value (sends 0 when cleared)
 */
export const mapClearableField = (value: number | null | undefined | string): number => {
  if (value === null || value === '' || value === 0) {
    return 0;
  }
  if (typeof value === 'number' && value > 0) {
    return value;
  }
  return 0;
};

/**
 * Maps a clearable string field (sends null when cleared)
 */
export const mapClearableStringField = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined || value.trim() === '') {
    return null;
  }
  return value.trim();
};

/**
 * Normalizes time zone value (trims whitespace or returns null)
 */
export const normalizeTimeZone = (timeZone: string | null | undefined): string | null => {
  if (timeZone == null || String(timeZone).trim() === '') {
    return null;
  }
  return String(timeZone).trim();
};

// Helper functions for mapFormDataToUpdateInputWithoutTimeFields

/**
 * Detects what type of changes are being made in the form data
 */
export const detectChangeTypes = (formData: JobFormData) => {
  const hasCrewChanges = (formData.crewMembersToAdd?.length ?? 0) > 0 || (formData.crewMembersToDelete?.length ?? 0) > 0;

  const hasFlagChanges =
    formData.specialFlag1Id !== undefined ||
    formData.specialFlag2Id !== undefined ||
    formData.specialFlag3Id !== undefined ||
    formData.jobInformationId !== undefined;

  const hasEquipmentChanges = (formData.equipmentsToAdd?.length ?? 0) > 0 || (formData.equipmentsToDelete?.length ?? 0) > 0;

  const hasExtraSystemsChanges = (formData.extraSystemsToAdd?.length ?? 0) > 0 || (formData.extraSystemsToDelete?.length ?? 0) > 0;

  return { hasCrewChanges, hasFlagChanges, hasEquipmentChanges, hasExtraSystemsChanges };
};

/**
 * Gets jobInformationId with default fallback to 1 (required by backend)
 */
export const getJobInformationId = (formData: JobFormData): number => {
  if (formData.jobInformationId !== undefined && typeof formData.jobInformationId === 'number' && formData.jobInformationId > 0) {
    return formData.jobInformationId;
  }
  return 1; // Default value
};

/**
 * Maps form data to crew-only update payload
 */
export const mapCrewOnlyUpdate = (formData: JobFormData & { id: number }): Input_V2_Mutations_RaptorJob_UpdateRaptorJobMutationInput => {
  return {
    id: formData.id,
    jobInformationId: getJobInformationId(formData),
    ...(formData.crewMembersToAdd && formData.crewMembersToAdd.length > 0 && { crewMembersToAdd: formData.crewMembersToAdd }),
    ...(formData.crewMembersToDelete && formData.crewMembersToDelete.length > 0 && { crewMembersToDelete: formData.crewMembersToDelete }),
    ...(formData.estimatedHours !== undefined &&
      formData.estimatedHours !== null &&
      Number(formData.estimatedHours) > 0 && { estimatedHours: Number(formData.estimatedHours) }),
    timeZone: normalizeTimeZone(formData.timeZone)
  };
};

/**
 * Maps form data to flag-only update payload
 */
export const mapFlagOnlyUpdate = (formData: JobFormData & { id: number }): Input_V2_Mutations_RaptorJob_UpdateRaptorJobMutationInput => {
  return {
    id: formData.id,
    ...(formData.jobInformationId !== undefined && {
      jobInformationId: mapClearableField(formData.jobInformationId)
    }),
    ...(formData.specialFlag1Id !== undefined && {
      specialFlag1Id: mapClearableField(formData.specialFlag1Id)
    }),
    ...(formData.specialFlag2Id !== undefined && {
      specialFlag2Id: mapClearableField(formData.specialFlag2Id)
    }),
    ...(formData.specialFlag3Id !== undefined && {
      specialFlag3Id: mapClearableField(formData.specialFlag3Id)
    })
  };
};

/**
 * Maps form data to full update payload (includes all fields except time fields).
 * Used for mixed changes or scenarios that don't fit crew-only or flag-only patterns.
 */
export const mapFullUpdate = (formData: JobFormData & { id: number }): Input_V2_Mutations_RaptorJob_UpdateRaptorJobMutationInput => {
  const callerId = normalizeCallerId(formData.callerId, { preserveUndefined: true });

  return {
    id: formData.id,

    // Customer Information
    ...(formData.customerId && { customerId: formData.customerId }),
    ...(callerId !== undefined && { callerId }),
    // Job Information
    ...(formData.jobName !== undefined && { jobName: mapClearableStringField(formData.jobName) }),
    ...(formData.jobSiteId && { jobSiteId: Number(formData.jobSiteId) }),
    ...(formData.typeId !== undefined && {
      typeId: formData.typeId === null || formData.typeId === 0 ? 0 : formData.typeId
    }),
    ...(formData.sizeRequestedId !== undefined && {
      sizeRequestedId: formData.sizeRequestedId === null || formData.sizeRequestedId === 0 ? 0 : formData.sizeRequestedId
    }),
    ...(formData.dispatchLocationId !== undefined && {
      dispatchLocationId: mapClearableField(formData.dispatchLocationId)
    }),
    ...(formData.jobInformationId !== undefined && {
      jobInformationId: mapClearableField(formData.jobInformationId)
    }),
    ...(formData.jobStatusId !== undefined && {
      jobStatusId: formData.jobStatusId === null || formData.jobStatusId === 0 ? 0 : formData.jobStatusId
    }),
    ...(formData.cod !== undefined && { cod: formData.cod }),

    // Order and Dispatch Information
    ...(formData.dispatcherId !== undefined && {
      dispatcherId: mapClearableField(formData.dispatcherId)
    }),
    ...(formData.salesAgentId !== undefined && {
      salesAgentId: mapClearableField(formData.salesAgentId)
    }),
    ...(formData.vendorId !== undefined && {
      vendorId: mapClearableField(formData.vendorId)
    }),
    ...(formData.poNumber !== undefined && { poNumber: mapClearableStringField(formData.poNumber) }),

    // Special Flags
    ...(formData.specialFlag1Id !== undefined && {
      specialFlag1Id: mapClearableField(formData.specialFlag1Id)
    }),
    ...(formData.specialFlag2Id !== undefined && {
      specialFlag2Id: mapClearableField(formData.specialFlag2Id)
    }),
    ...(formData.specialFlag3Id !== undefined && {
      specialFlag3Id: mapClearableField(formData.specialFlag3Id)
    }),

    // Date Information (only startDate, no time fields)
    // Include startDate in fallback path (general case) - exclusion only applies to crew/flag-only updates
    // Time fields are intentionally excluded: startTime, onJobTime, leaveTime, showUpTime
    // estimatedHours is intentionally excluded when 0 or undefined to avoid triggering backend default value logic
    ...(formData.startDate !== undefined && { startDate: validateDateField(formData.startDate) }),
    ...(formData.estimatedHours !== undefined &&
      formData.estimatedHours !== null &&
      Number(formData.estimatedHours) > 0 && { estimatedHours: Number(formData.estimatedHours) }),
    // Send job's time zone from backend, or null when job has no time zone (e.g. "", null, undefined)
    timeZone: normalizeTimeZone(formData.timeZone),

    // Notes
    ...(formData.dispatchNotes !== undefined && { dispatchNotes: mapClearableStringField(formData.dispatchNotes) }),
    ...(formData.notesForCrew !== undefined && { notesForCrew: mapClearableStringField(formData.notesForCrew) }),

    // Resources
    ...(formData.equipmentsToAdd && formData.equipmentsToAdd.length > 0 && { equipmentToAdd: formData.equipmentsToAdd }),
    ...(formData.equipmentsToDelete && formData.equipmentsToDelete.length > 0 && { equipmentToDelete: formData.equipmentsToDelete }),

    ...(formData.crewMembersToAdd && formData.crewMembersToAdd.length > 0 && { crewMembersToAdd: formData.crewMembersToAdd }),
    ...(formData.crewMembersToDelete && formData.crewMembersToDelete.length > 0 && { crewMembersToDelete: formData.crewMembersToDelete }),
    ...(formData.extraSystemsToAdd && formData.extraSystemsToAdd.length > 0 && { extraSystemsToAdd: formData.extraSystemsToAdd }),
    ...(formData.extraSystemsToDelete &&
      formData.extraSystemsToDelete.length > 0 && { extraSystemsToDelete: formData.extraSystemsToDelete })
  };
};
